/**
 * LogbookTab.js
 *
 * Main Logbook tab - the vessel's official record
 *
 * Features:
 * - Date-based navigation
 * - Entry list with type icons
 * - Manual entry creation
 * - Search and filter
 * - Export capabilities
 */

import React, { useState, useCallback, useMemo } from 'react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import {
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  PlusCircle,
  Search,
  Download,
  Filter,
  Anchor,
  AlertTriangle,
  Fuel,
  AlertCircle,
  Siren,
  Users,
  Wrench,
  FileText,
  Clock,
  X,
  ShieldAlert,
  UserCheck,
  Clock4,
  Award,
  CheckSquare,
  ClipboardCheck,
  CheckCircle,
  Lock,
  Pen,
  ExternalLink,
  Ship,
  MapPin,
  Thermometer
} from 'lucide-react';

// Components
// FT-196: Replaced SignaturePad with ConfirmSignOffModal (Pattern 1: confirm-only)
import ConfirmSignOffModal from '../../../shared/ConfirmSignOffModal';
import { getCreatedByName } from '../../shared/AttributionBadge';

// Config
import {
  LOGBOOK_ENTRY_TYPES,
  LOGBOOK_ENTRY_TYPE_LABELS,
  MANUAL_ENTRY_CATEGORIES,
  MANUAL_ENTRY_CATEGORY_LABELS,
  EXPORT_FORMATS,
  EXPORT_FORMAT_LABELS
} from '../../../../config/operationsConfig';
import { portLabel } from '../../../../utils/voyage/portLabel';

// Icon mapping for entry types
const ENTRY_TYPE_ICONS = {
  [LOGBOOK_ENTRY_TYPES.VOYAGE]: Anchor,
  [LOGBOOK_ENTRY_TYPES.DRILL]: AlertTriangle,
  [LOGBOOK_ENTRY_TYPES.BUNKERING]: Fuel,
  [LOGBOOK_ENTRY_TYPES.DEFECT]: AlertCircle,
  [LOGBOOK_ENTRY_TYPES.EMERGENCY]: Siren,
  [LOGBOOK_ENTRY_TYPES.CREW_CHANGE]: Users,
  [LOGBOOK_ENTRY_TYPES.MAINTENANCE]: Wrench,
  [LOGBOOK_ENTRY_TYPES.MANUAL]: FileText,
  [LOGBOOK_ENTRY_TYPES.INCIDENT]: ShieldAlert,
  [LOGBOOK_ENTRY_TYPES.FAMILIARISATION]: UserCheck,
  [LOGBOOK_ENTRY_TYPES.HOR_VIOLATION]: Clock4,
  [LOGBOOK_ENTRY_TYPES.CERTIFICATE]: Award,
  [LOGBOOK_ENTRY_TYPES.SIGN_OFF]: CheckSquare,
  [LOGBOOK_ENTRY_TYPES.PERMIT]: ClipboardCheck,
  [LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED]: CheckCircle
};

// Color mapping for entry types
const ENTRY_TYPE_COLORS = {
  [LOGBOOK_ENTRY_TYPES.VOYAGE]: 'blue',
  [LOGBOOK_ENTRY_TYPES.DRILL]: 'amber',
  [LOGBOOK_ENTRY_TYPES.BUNKERING]: 'purple',
  [LOGBOOK_ENTRY_TYPES.DEFECT]: 'orange',
  [LOGBOOK_ENTRY_TYPES.EMERGENCY]: 'red',
  [LOGBOOK_ENTRY_TYPES.CREW_CHANGE]: 'green',
  [LOGBOOK_ENTRY_TYPES.MAINTENANCE]: 'slate',
  [LOGBOOK_ENTRY_TYPES.MANUAL]: 'gray',
  [LOGBOOK_ENTRY_TYPES.INCIDENT]: 'rose',
  [LOGBOOK_ENTRY_TYPES.FAMILIARISATION]: 'teal',
  [LOGBOOK_ENTRY_TYPES.HOR_VIOLATION]: 'yellow',
  [LOGBOOK_ENTRY_TYPES.CERTIFICATE]: 'indigo',
  [LOGBOOK_ENTRY_TYPES.SIGN_OFF]: 'emerald',
  [LOGBOOK_ENTRY_TYPES.PERMIT]: 'cyan',
  [LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED]: 'lime'
};

/**
 * LogbookTab Component
 */
const LogbookTab = ({ context }) => {
  const { logbook, showAlert, showConfirm, currentUser } = context;
  const {
    getEntriesForDate,
    getUnsignedEntriesForDate,
    getEntriesForRange,
    searchEntries,
    addManualEntry,
    editEntry,
    deleteEntry,
    exportLogbook,
    getStats,
    signOffDay
  } = logbook;

  // Date navigation
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // UI state
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showSignOffDay, setShowSignOffDay] = useState(false);

  // FT-232: Filter mode state
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterPreset, setFilterPreset] = useState('today'); // 'today' | 'week' | 'month' | 'custom'
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null
  });

  // Manual entry form
  const [newEntry, setNewEntry] = useState({
    content: '',
    category: MANUAL_ENTRY_CATEGORIES.GENERAL,
    notes: ''
  });

  // FT-196: Removed signature pad state - now using ConfirmSignOffModal (Pattern 1)

  // Get entries for selected date
  const entriesForDate = useMemo(() => {
    return getEntriesForDate(selectedDate);
  }, [getEntriesForDate, selectedDate]);

  // Get stats
  const stats = useMemo(() => getStats(), [getStats]);

  // Format date for display
  const formatDateDisplay = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    }
  }, []);

  // Get initials for logbook entry
  // FT-123: Show actual user initials for ALL entries including auto-logged ones
  const getEntryInitials = useCallback((entry) => {
    // First try to get the actual user name from createdBy
    const userName = getCreatedByName(entry.createdBy);
    if (userName && userName !== 'System' && userName !== 'Unknown') {
      // Extract initials from name (e.g., "John Smith" -> "JS")
      return userName
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 3)
        .join('');
    }
    // Only fall back to SYS if we truly don't have a user name
    if (entry.isAuto) {
      return 'SYS';
    }
    return '--';
  }, []);

  // Navigate dates
  const navigatePreviousDay = useCallback(() => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }, []);

  const navigateNextDay = useCallback(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      // Don't go beyond today
      if (newDate > new Date()) {
        return prev;
      }
      return newDate;
    });
  }, []);

  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // Handle adding manual entry
  const handleAddManualEntry = useCallback(async () => {
    if (!newEntry.content.trim()) {
      await showAlert('Error', 'Please enter some content for the log entry.', 'warning');
      return;
    }

    const result = await addManualEntry({
      content: newEntry.content,
      category: newEntry.category,
      notes: newEntry.notes,
      timestamp: new Date().toISOString()
    });

    if (result) {
      setNewEntry({ content: '', category: MANUAL_ENTRY_CATEGORIES.GENERAL, notes: '' });
      setShowAddEntry(false);
    }
  }, [newEntry, addManualEntry, showAlert]);

  // Handle search (kept for backwards compatibility with existing code)
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchEntries(searchQuery);
  }, [searchQuery, searchEntries]);

  // FT-232: Compute date range based on filter preset
  const getFilterDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    switch (filterPreset) {
      case 'today': {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        return { start, end: today };
      }
      case 'week': {
        const start = new Date();
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { start, end: today };
      }
      case 'month': {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        return { start, end: today };
      }
      case 'custom': {
        return {
          start: customDateRange.from ? new Date(customDateRange.from) : new Date(),
          end: customDateRange.to ? new Date(customDateRange.to) : new Date()
        };
      }
      default:
        return { start: new Date(), end: today };
    }
  }, [filterPreset, customDateRange]);

  // FT-232: Get filtered entries for filter mode
  const filteredEntries = useMemo(() => {
    if (!isFilterMode) return [];

    const { start, end } = getFilterDateRange();
    let entries = getEntriesForRange(start, end);

    // Apply keyword filter if present
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      entries = entries.filter(entry => {
        const title = (entry.title || '').toLowerCase();
        const summary = (entry.summary || '').toLowerCase();
        const notes = (entry.data?.notes || '').toLowerCase();
        return title.includes(term) || summary.includes(term) || notes.includes(term);
      });
    }

    return entries;
  }, [isFilterMode, getFilterDateRange, getEntriesForRange, searchQuery]);

  // FT-232: Group filtered entries by date for display
  const groupedFilteredEntries = useMemo(() => {
    if (!isFilterMode || filteredEntries.length === 0) return [];

    const groups = {};
    filteredEntries.forEach(entry => {
      const dateKey = new Date(entry.timestamp).toISOString().split('T')[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });

    // Sort by date descending (newest first)
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, entries]) => ({
        date: new Date(dateKey),
        entries: entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      }));
  }, [isFilterMode, filteredEntries]);

  // FT-232: Count unique days in filter results
  const filterDaysCount = useMemo(() => {
    return groupedFilteredEntries.length;
  }, [groupedFilteredEntries]);

  // Render date navigation header
  const renderDateHeader = () => (
    <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <button
        onClick={navigatePreviousDay}
        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      <button
        onClick={() => setShowDatePicker(true)}
        className="flex items-center gap-2 px-4 py-2 min-h-[44px] hover:bg-gray-100 rounded-lg"
      >
        <Calendar className="h-5 w-5 text-gray-400" />
        <span className="font-medium text-gray-900">
          {formatDateDisplay(selectedDate)}
        </span>
      </button>

      <div className="flex items-center gap-2">
        <button
          onClick={navigateNextDay}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          disabled={selectedDate.toDateString() === new Date().toDateString()}
        >
          <ChevronRight className={`h-5 w-5 ${
            selectedDate.toDateString() === new Date().toDateString()
              ? 'text-gray-300'
              : ''
          }`} />
        </button>

        {selectedDate.toDateString() !== new Date().toDateString() && (
          <button
            onClick={goToToday}
            className="px-3 min-h-[44px] text-sm text-cyan-600 hover:text-cyan-700"
          >
            Today
          </button>
        )}
      </div>
    </div>
  );

  // Render toolbar
  const renderToolbar = () => (
    <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {/* FT-232: Search button toggles filter mode */}
        <button
          onClick={() => {
            if (isFilterMode) {
              // Exit filter mode - return to browse at today
              setIsFilterMode(false);
              setSearchQuery('');
              setFilterPreset('today');
              setCustomDateRange({ from: null, to: null });
              setSelectedDate(new Date());
            } else {
              // Enter filter mode
              setIsFilterMode(true);
            }
          }}
          className={`p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg ${
            isFilterMode ? 'bg-cyan-100 text-cyan-600' : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <Search className="h-5 w-5" />
        </button>

        <button
          onClick={() => setShowExport(true)}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {/* Sign Off Day Button - FT-232: hide in filter mode */}
        {!isFilterMode && (
          <button
            onClick={() => setShowSignOffDay(true)}
            className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Pen className="h-4 w-4" />
            <span className="text-sm font-medium hidden sm:inline">Sign Off Day</span>
          </button>
        )}

        <button
          onClick={() => setShowAddEntry(true)}
          className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
        >
          <Plus className="h-4 w-4" />
          <span className="text-sm font-medium">Add Entry</span>
        </button>
      </div>
    </div>
  );

  // FT-232: Filter mode bar with date presets and custom range
  const renderFilterBar = () => {
    if (!isFilterMode) return null;

    const presets = [
      { key: 'today', label: 'Today' },
      { key: 'week', label: 'This Week' },
      { key: 'month', label: 'This Month' },
      { key: 'custom', label: 'Custom' }
    ];

    return (
      <div className="bg-white border-b border-gray-200 px-4 py-3 space-y-3">
        {/* Header with exit button */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setIsFilterMode(false);
              setSearchQuery('');
              setFilterPreset('today');
              setCustomDateRange({ from: null, to: null });
              setSelectedDate(new Date());
            }}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Log
          </button>
          <p className="text-sm font-medium text-gray-700">Filter Entries</p>
        </div>

        {/* Date Presets */}
        <div className="flex items-center gap-2 flex-wrap">
          {presets.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilterPreset(key)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                filterPreset === key
                  ? 'border-cyan-600 bg-cyan-50 text-cyan-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom Date Range (only when Custom selected) */}
        {filterPreset === 'custom' && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">From:</label>
              <input
                type="date"
                value={customDateRange.from || ''}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">To:</label>
              <input
                type="date"
                value={customDateRange.to || ''}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>
        )}

        {/* Search Input (always visible in filter mode) */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search entries by keyword..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render entry item
  const renderEntry = (entry) => {
    const EntryIcon = ENTRY_TYPE_ICONS[entry.type] || FileText;
    const color = ENTRY_TYPE_COLORS[entry.type] || 'gray';
    const time = new Date(entry.timestamp);

    // Get category label for manual entries
    const categoryLabel = entry.category && entry.type === LOGBOOK_ENTRY_TYPES.MANUAL
      ? MANUAL_ENTRY_CATEGORY_LABELS[entry.category]
      : null;

    return (
      <button
        key={entry.id}
        onClick={() => setSelectedEntry(entry)}
        className="lg:hidden w-full px-4 py-3 flex items-start gap-3 bg-white hover:bg-gray-50 text-left border-b border-gray-100 last:border-b-0"
      >
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <EntryIcon className={`h-5 w-5 text-${color}-600`} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Row 1: Title + Category Badge + Time */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {entry.title || LOGBOOK_ENTRY_TYPE_LABELS[entry.type]}
              </p>
              {categoryLabel && (
                <span className="inline-flex items-center rounded-full bg-cyan-50 text-cyan-700 text-xs px-2 py-0.5 whitespace-nowrap">
                  {categoryLabel}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {time.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {/* Row 2: Summary */}
          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
            {entry.summary}
          </p>

          {/* Row 3: Position data (if applicable) */}
          {entry.type === 'position' && entry.data?.lat && entry.data?.lon && (
            <p className="text-xs font-mono text-gray-500 mt-1">
              {Math.abs(entry.data.lat).toFixed(4)}{entry.data.lat >= 0 ? 'N' : 'S'}, {Math.abs(entry.data.lon).toFixed(4)}{entry.data.lon >= 0 ? 'E' : 'W'}
              {entry.data.accuracy && ` (±${Math.round(entry.data.accuracy)}m)`}
            </p>
          )}

          {/* Row 4: Metadata - Recorded by OR Auto-logged */}
          <div className="flex items-center gap-2 mt-1.5">
            {entry.isAuto ? (
              <span className="text-xs text-gray-400">
                Auto-logged from {entry.sourceModule}
              </span>
            ) : (
              <span className="text-xs text-gray-500">
                <Users className="inline h-3 w-3 mr-1" />
                {getCreatedByName(entry.createdBy) || 'Unknown'}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  // Render entry as table row (responsive: hides No. and Initials on mobile)
  const renderEntryRow = (entry, index) => {
    const time = new Date(entry.timestamp);
    const typeLabel = LOGBOOK_ENTRY_TYPE_LABELS[entry.type] || 'Entry';

    return (
      <tr
        key={entry.id}
        onClick={() => setSelectedEntry(entry)}
        className={`cursor-pointer hover:bg-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${entry.isLocked ? 'border-l-4 border-emerald-500' : ''}`}
      >
        {/* Entry number - hidden on mobile */}
        <td className="hidden sm:table-cell px-3 py-2 text-sm text-gray-600 text-center font-medium">
          {index + 1}
        </td>
        {/* Time - always visible */}
        <td className="px-2 sm:px-3 py-2 text-sm font-mono text-gray-900 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {entry.isLocked && <Lock className="h-3 w-3 text-emerald-600" />}
          </div>
        </td>
        {/* Entry content - always visible, stacks type above content on mobile */}
        <td className="px-2 sm:px-3 py-2 text-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:gap-2">
            <span className="text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap mb-0.5 sm:mb-0">
              [{typeLabel}]
            </span>
            <div className="min-w-0">
              <p className="text-gray-900 font-medium line-clamp-2 sm:truncate">
                {entry.title || typeLabel}
              </p>
              <p className="text-gray-600 text-sm line-clamp-2 sm:line-clamp-1">
                {entry.summary}
              </p>
            </div>
          </div>
        </td>
        {/* Initials - hidden on mobile */}
        <td className="hidden sm:table-cell px-3 py-2 text-sm font-mono text-gray-700 text-center">
          {getEntryInitials(entry)}
        </td>
      </tr>
    );
  };

  // Render formal ledger table (desktop only)
  const renderEntriesTable = () => {
    const entriesToShow = searchQuery ? searchResults : entriesForDate;

    if (entriesToShow.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BookOpen className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">
            {searchQuery
              ? 'No entries found'
              : 'No entries for this date'
            }
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {searchQuery
              ? 'Try a different search term'
              : 'Log entries will appear here automatically'
            }
          </p>
        </div>
      );
    }

    // Format date for formal header
    const formalDate = selectedDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    return (
      <div className="p-4">
        {/* Formal Logbook Container */}
        <div className="bg-white border-2 border-gray-800 rounded-lg overflow-hidden">
          {/* Formal Header - responsive */}
          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b-2 border-gray-800 bg-gray-50">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide">
                  Official Log
                </h2>
                <p className="text-xs sm:text-sm text-gray-600">
                  {context?.vessel?.name || 'Vessel'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs sm:text-sm font-semibold text-gray-900">{formalDate}</p>
                <p className="text-xs text-gray-500">{entriesToShow.length} Entries</p>
              </div>
            </div>
          </div>

          {/* Table - responsive columns */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-800 bg-gray-100">
                {/* No. column - hidden on mobile */}
                <th className="hidden sm:table-cell px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                  No.
                </th>
                {/* Time column - always visible */}
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-16 sm:w-20">
                  Time
                </th>
                {/* Entry column - always visible */}
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Entry
                </th>
                {/* Initials column - hidden on mobile */}
                <th className="hidden sm:table-cell px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                  Initials
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {entriesToShow.map((entry, index) => renderEntryRow(entry, index))}
            </tbody>
          </table>

          {/* FT-130: Sign-Off Summary - only show in daily view (not search), and only show ONE collapsed summary
               Sign-off entries appear as table rows like other entries. The signature image is only visible
               when the user clicks the entry to open the detail view.
               FT-196 FIX: Only show for DAILY sign-offs (entries that locked logbook entries).
               Voyage sign-offs (signatureType='voyage_signoff') should NOT show this bar. */}
          {!searchQuery && (() => {
            // Find DAILY SIGN_OFF entries for this date (NOT voyage sign-offs)
            // Daily sign-offs have signedEntryIds (list of entries they locked)
            // Voyage sign-offs have signatureType='voyage_signoff'
            const signOffEntries = entriesToShow.filter(
              entry => entry.type === LOGBOOK_ENTRY_TYPES.SIGN_OFF &&
                       entry.data?.signatureType !== 'voyage_signoff' &&
                       entry.data?.signedEntryIds // Must have locked entries
            );

            if (signOffEntries.length === 0) return null;

            // Get the most recent sign-off for summary display
            const latestSignOff = signOffEntries[signOffEntries.length - 1];
            const displayTimestamp = latestSignOff.data?.actualSignedAt || latestSignOff.timestamp;
            const signedTime = new Date(displayTimestamp);
            const formattedTime = signedTime.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) + ' ' + signedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            // FT-130: Collapsed one-line summary - click to see signature in entry detail
            return (
              <div
                className="border-t-2 border-gray-800 bg-emerald-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-emerald-100"
                onClick={() => setSelectedEntry(latestSignOff)}
              >
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">
                    Daily Sign-Off
                  </span>
                  {signOffEntries.length > 1 && (
                    <span className="text-xs text-emerald-600">
                      (+{signOffEntries.length - 1} amendment{signOffEntries.length > 2 ? 's' : ''})
                    </span>
                  )}
                </div>
                <div className="text-sm text-emerald-700">
                  {latestSignOff.data?.signedBy || 'Master'} • {formattedTime}
                </div>
              </div>
            );
          })()}

          {/* Footer */}
          <div className="px-4 py-3 border-t-2 border-gray-800 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              End of entries for {formalDate}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // FT-232: Render grouped filter results
  const renderFilterResults = () => {
    if (filteredEntries.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">No entries found</p>
          <p className="text-sm text-gray-400 mt-1">
            Try adjusting your date range or search terms
          </p>
        </div>
      );
    }

    return (
      <div className="p-4 space-y-4">
        {/* Results Summary */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-700 font-medium">
            {filteredEntries.length} entr{filteredEntries.length === 1 ? 'y' : 'ies'} across {filterDaysCount} day{filterDaysCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Grouped Results */}
        {groupedFilteredEntries.map(({ date, entries }) => (
          <div key={date.toISOString()} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Date Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">
                {date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              <span className="text-xs text-gray-500">
                {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
              </span>
            </div>

            {/* Entries in this date group */}
            <div className="divide-y divide-gray-100">
              {entries.map(entry => {
                const EntryIcon = ENTRY_TYPE_ICONS[entry.type] || FileText;
                const color = ENTRY_TYPE_COLORS[entry.type] || 'gray';
                const time = new Date(entry.timestamp);

                return (
                  <button
                    key={entry.id}
                    onClick={() => setSelectedEntry(entry)}
                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 text-left"
                  >
                    <div className={`p-2 rounded-lg bg-${color}-100`}>
                      <EntryIcon className={`h-4 w-4 text-${color}-600`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">
                            [{LOGBOOK_ENTRY_TYPE_LABELS[entry.type] || 'Entry'}]
                          </span>
                          <p className="font-medium text-gray-900 truncate text-sm">
                            {entry.title || LOGBOOK_ENTRY_TYPE_LABELS[entry.type]}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {time.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">
                        {entry.summary}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // FT-232: Route to appropriate renderer based on mode
  const renderEntries = () => {
    if (isFilterMode) {
      return renderFilterResults();
    }
    return renderEntriesTable();
  };

  // Render entry detail panel (slide-out)
  const renderEntryDetail = () => {
    if (!selectedEntry) return null;

    const EntryIcon = ENTRY_TYPE_ICONS[selectedEntry.type] || FileText;
    const color = ENTRY_TYPE_COLORS[selectedEntry.type] || 'gray';
    const timestamp = new Date(selectedEntry.timestamp);
    const categoryLabel = selectedEntry.category
      ? MANUAL_ENTRY_CATEGORY_LABELS[selectedEntry.category]
      : null;

    // Format full date/time
    const formatFullDateTime = (date) => {
      return date.toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => setSelectedEntry(null)}
        />

        {/* Panel */}
        <div className="relative w-full max-w-lg max-h-[85vh] overflow-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-xl">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${color}-100`}>
                <EntryIcon className={`h-5 w-5 text-${color}-600`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {selectedEntry.title || LOGBOOK_ENTRY_TYPE_LABELS[selectedEntry.type]}
                </h3>
                <p className="text-xs text-gray-500">{formatFullDateTime(timestamp)}</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedEntry(null)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Category Badge (for manual entries) */}
            {categoryLabel && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Category</p>
                <span className="inline-flex items-center rounded-full bg-cyan-50 text-cyan-700 text-sm px-3 py-1">
                  {categoryLabel}
                </span>
              </div>
            )}

            {/* Summary/Content */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Entry Content</p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {selectedEntry.summary || selectedEntry.data?.content || 'No content'}
                </p>
              </div>
            </div>

            {/* Notes (if present) */}
            {selectedEntry.data?.notes && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Additional Notes</p>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedEntry.data.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Position Data (if applicable) */}
            {selectedEntry.data?.lat && selectedEntry.data?.lon && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Position</p>
                <p className="text-sm font-mono text-gray-900">
                  {Math.abs(selectedEntry.data.lat).toFixed(4)}{selectedEntry.data.lat >= 0 ? 'N' : 'S'},
                  {Math.abs(selectedEntry.data.lon).toFixed(4)}{selectedEntry.data.lon >= 0 ? 'E' : 'W'}
                  {selectedEntry.data.accuracy && ` (±${Math.round(selectedEntry.data.accuracy)}m)`}
                </p>
              </div>
            )}

            {/* FT-24: Type-specific detail renderers */}

            {/* DRILL entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.DRILL && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Drill Report</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.drillType && (
                      <div className="col-span-2">
                        <dt className="text-amber-600 text-xs">Type</dt>
                        <dd className="font-medium text-amber-900">{selectedEntry.data.drillType}</dd>
                      </div>
                    )}
                    {selectedEntry.data.outcome && (
                      <div>
                        <dt className="text-amber-600 text-xs">Outcome</dt>
                        <dd className={`font-medium ${selectedEntry.data.outcome?.toLowerCase() === 'successful' || selectedEntry.data.outcome?.toLowerCase() === 'passed' ? 'text-green-700' : 'text-red-700'}`}>
                          {selectedEntry.data.outcome}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.duration && (
                      <div>
                        <dt className="text-amber-600 text-xs">Duration</dt>
                        <dd className="font-medium text-amber-900">{selectedEntry.data.duration} mins</dd>
                      </div>
                    )}
                    {selectedEntry.data.participants && selectedEntry.data.participants.length > 0 && (
                      <div className="col-span-2">
                        <dt className="text-amber-600 text-xs">Participants ({selectedEntry.data.participants.length})</dt>
                        <dd className="text-amber-900">{selectedEntry.data.participants.map(p => typeof p === 'string' ? p : p.name).join(', ')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Safety → Drills</span>
                </div>
              </div>
            )}

            {/* DEFECT entry details */}
            {(selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT || selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED) && selectedEntry.data && (
              <div className="space-y-3">
                <div className={`rounded-lg p-3 border ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-sm font-medium ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-800' : 'text-red-800'}`}>
                      {selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'Defect Resolved' : 'Defect Raised'}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {(selectedEntry.data.description || selectedEntry.data.title) && (
                      <div className="col-span-2">
                        <dt className={`text-xs ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-600' : 'text-red-600'}`}>Description</dt>
                        <dd className={`font-medium ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-900' : 'text-red-900'}`}>
                          {selectedEntry.data.description || selectedEntry.data.title}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.priority && (
                      <div>
                        <dt className={`text-xs ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-600' : 'text-red-600'}`}>Priority</dt>
                        <dd className={`font-medium ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-900' : 'text-red-900'}`}>{selectedEntry.data.priority}</dd>
                      </div>
                    )}
                    {selectedEntry.data.category && (
                      <div>
                        <dt className={`text-xs ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-600' : 'text-red-600'}`}>Category</dt>
                        <dd className={`font-medium ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-900' : 'text-red-900'}`}>{selectedEntry.data.category}</dd>
                      </div>
                    )}
                    {selectedEntry.data.location && (
                      <div className="col-span-2">
                        <dt className={`text-xs ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-600' : 'text-red-600'}`}>Location</dt>
                        <dd className={`font-medium ${selectedEntry.type === LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED ? 'text-green-900' : 'text-red-900'}`}>{selectedEntry.data.location}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Equipment → Defects</span>
                </div>
              </div>
            )}

            {/* INCIDENT entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.INCIDENT && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Incident Report</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.incidentType && (
                      <div className="col-span-2">
                        <dt className="text-purple-600 text-xs">Type</dt>
                        <dd className="font-medium text-purple-900">{selectedEntry.data.incidentType}</dd>
                      </div>
                    )}
                    {selectedEntry.data.severity && (
                      <div>
                        <dt className="text-purple-600 text-xs">Severity</dt>
                        <dd className={`font-medium ${selectedEntry.data.severity === 'critical' ? 'text-red-700' : selectedEntry.data.severity === 'high' ? 'text-orange-700' : 'text-purple-900'}`}>
                          {selectedEntry.data.severity.charAt(0).toUpperCase() + selectedEntry.data.severity.slice(1)}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.injuries !== undefined && (
                      <div>
                        <dt className="text-purple-600 text-xs">Injuries</dt>
                        <dd className="font-medium text-purple-900">{selectedEntry.data.injuries || 'None'}</dd>
                      </div>
                    )}
                    {selectedEntry.data.description && (
                      <div className="col-span-2">
                        <dt className="text-purple-600 text-xs">Description</dt>
                        <dd className="text-purple-900">{selectedEntry.data.description}</dd>
                      </div>
                    )}
                    {selectedEntry.data.immediateActions && (
                      <div className="col-span-2">
                        <dt className="text-purple-600 text-xs">Immediate Actions</dt>
                        <dd className="text-purple-900">{selectedEntry.data.immediateActions}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Safety → Incidents</span>
                </div>
              </div>
            )}

            {/* VOYAGE entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.VOYAGE && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Ship className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Voyage Details</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {(selectedEntry.data.from || selectedEntry.data.to) && (
                      <div className="col-span-2">
                        <dt className="text-blue-600 text-xs">Route</dt>
                        <dd className="font-medium text-blue-900">
                          {portLabel(selectedEntry.data.from, '—')} → {portLabel(selectedEntry.data.to, '—')}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.duration && (
                      <div>
                        <dt className="text-blue-600 text-xs">Duration</dt>
                        <dd className="font-medium text-blue-900">{selectedEntry.data.duration}</dd>
                      </div>
                    )}
                    {(selectedEntry.data.crewCount !== undefined || selectedEntry.data.passengerCount !== undefined) && (
                      <div>
                        <dt className="text-blue-600 text-xs">POB</dt>
                        <dd className="font-medium text-blue-900">
                          {Number(selectedEntry.data.crewCount || 0) + Number(selectedEntry.data.passengerCount || 0)}
                          {/* React renders numbers — including 0 — as text. Original guard was
                              `crewCount && passengerCount && "(...)"` which evaluates to 0
                              (number) when passengerCount === 0, and React renders the 0 as
                              the literal "0" character. Result: "POB" "2" "0" = "POB 20".
                              Coerce to boolean so falsy short-circuit returns false, which
                              React hides. */}
                          {!!(Number(selectedEntry.data.crewCount) && Number(selectedEntry.data.passengerCount)) &&
                            ` (${Number(selectedEntry.data.crewCount)} crew, ${Number(selectedEntry.data.passengerCount)} pax)`}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.operations && selectedEntry.data.operations.length > 0 && (
                      <div className="col-span-2">
                        <dt className="text-blue-600 text-xs">Operations ({selectedEntry.data.operations.length})</dt>
                        <dd className="text-blue-900">{selectedEntry.data.operations.join(', ')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Operations → Voyage</span>
                </div>
              </div>
            )}

            {/* MAINTENANCE entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.MAINTENANCE && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-slate-100 rounded-lg p-3 border border-slate-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-800">Maintenance Completion</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.jobTitle && (
                      <div className="col-span-2">
                        <dt className="text-slate-500 text-xs">Job</dt>
                        <dd className="font-medium text-slate-900">{selectedEntry.data.jobTitle}</dd>
                      </div>
                    )}
                    {selectedEntry.data.equipmentType && (
                      <div>
                        <dt className="text-slate-500 text-xs">Equipment</dt>
                        <dd className="font-medium text-slate-900">{selectedEntry.data.equipmentType}</dd>
                      </div>
                    )}
                    {selectedEntry.data.completedBy && (
                      <div>
                        <dt className="text-slate-500 text-xs">Completed By</dt>
                        <dd className="font-medium text-slate-900">
                          {typeof selectedEntry.data.completedBy === 'string' ? selectedEntry.data.completedBy : selectedEntry.data.completedBy?.name}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Equipment → Maintenance</span>
                </div>
              </div>
            )}

            {/* BUNKERING entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.BUNKERING && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Fuel className="h-4 w-4 text-yellow-700" />
                    <span className="text-sm font-medium text-yellow-800">Fuel Operation</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.fuelType && (
                      <div>
                        <dt className="text-yellow-600 text-xs">Fuel Type</dt>
                        <dd className="font-medium text-yellow-900">{selectedEntry.data.fuelType}</dd>
                      </div>
                    )}
                    {selectedEntry.data.quantity && (
                      <div>
                        <dt className="text-yellow-600 text-xs">Quantity</dt>
                        <dd className="font-medium text-yellow-900">{selectedEntry.data.quantity} L</dd>
                      </div>
                    )}
                    {selectedEntry.data.supplier && (
                      <div className="col-span-2">
                        <dt className="text-yellow-600 text-xs">Supplier</dt>
                        <dd className="font-medium text-yellow-900">{selectedEntry.data.supplier}</dd>
                      </div>
                    )}
                    {selectedEntry.data.spillage !== undefined && (
                      <div>
                        <dt className="text-yellow-600 text-xs">Spillage</dt>
                        <dd className={`font-medium ${selectedEntry.data.spillage ? 'text-red-700' : 'text-green-700'}`}>
                          {selectedEntry.data.spillage ? 'Yes' : 'None'}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            )}

            {/* CREW CHANGE entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.CREW_CHANGE && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm font-medium text-cyan-800">Crew Change</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.signOns && selectedEntry.data.signOns.length > 0 && (
                      <div className="col-span-2">
                        <dt className="text-cyan-600 text-xs">Sign On ({selectedEntry.data.signOns.length})</dt>
                        <dd className="text-cyan-900">
                          {selectedEntry.data.signOns.map(s => typeof s === 'string' ? s : `${s.name} (${s.role})`).join(', ')}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.signOffs && selectedEntry.data.signOffs.length > 0 && (
                      <div className="col-span-2">
                        <dt className="text-cyan-600 text-xs">Sign Off ({selectedEntry.data.signOffs.length})</dt>
                        <dd className="text-cyan-900">
                          {selectedEntry.data.signOffs.map(s => typeof s === 'string' ? s : `${s.name} (${s.role})`).join(', ')}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Crew → Manning</span>
                </div>
              </div>
            )}

            {/* PERMIT entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.PERMIT && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardCheck className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800">
                      Permit to Work {selectedEntry.data.status === 'closed' ? '(Closed)' : '(Issued)'}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.permitType && (
                      <div className="col-span-2">
                        <dt className="text-orange-600 text-xs">Type</dt>
                        <dd className="font-medium text-orange-900">{selectedEntry.data.permitType}</dd>
                      </div>
                    )}
                    {selectedEntry.data.location && (
                      <div>
                        <dt className="text-orange-600 text-xs">Location</dt>
                        <dd className="font-medium text-orange-900">{selectedEntry.data.location}</dd>
                      </div>
                    )}
                    {selectedEntry.data.validUntil && (
                      <div>
                        <dt className="text-orange-600 text-xs">Valid Until</dt>
                        <dd className="font-medium text-orange-900">
                          {new Date(selectedEntry.data.validUntil).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </dd>
                      </div>
                    )}
                    {selectedEntry.data.precautions && selectedEntry.data.precautions.length > 0 && (
                      <div className="col-span-2">
                        <dt className="text-orange-600 text-xs">Precautions</dt>
                        <dd className="text-orange-900">{selectedEntry.data.precautions.join(', ')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Operations → Checklists</span>
                </div>
              </div>
            )}

            {/* FAMILIARISATION entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.FAMILIARISATION && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="h-4 w-4 text-teal-600" />
                    <span className="text-sm font-medium text-teal-800">Familiarisation Complete</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.crewMember && (
                      <div className="col-span-2">
                        <dt className="text-teal-600 text-xs">Crew Member</dt>
                        <dd className="font-medium text-teal-900">{selectedEntry.data.crewMember}</dd>
                      </div>
                    )}
                    {selectedEntry.data.itemsCompleted && (
                      <div>
                        <dt className="text-teal-600 text-xs">Items Completed</dt>
                        <dd className="font-medium text-teal-900">{selectedEntry.data.itemsCompleted}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            )}

            {/* HOR VIOLATION entry details */}
            {selectedEntry.type === LOGBOOK_ENTRY_TYPES.HOR_VIOLATION && selectedEntry.data && (
              <div className="space-y-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock4 className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">Hours of Rest Violation</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    {selectedEntry.data.crewMember && (
                      <div className="col-span-2">
                        <dt className="text-red-600 text-xs">Crew Member</dt>
                        <dd className="font-medium text-red-900">{selectedEntry.data.crewMember}</dd>
                      </div>
                    )}
                    {selectedEntry.data.hoursWorked && (
                      <div>
                        <dt className="text-red-600 text-xs">Hours Worked</dt>
                        <dd className="font-medium text-red-900">{selectedEntry.data.hoursWorked}h</dd>
                      </div>
                    )}
                    {selectedEntry.data.restPeriod && (
                      <div>
                        <dt className="text-red-600 text-xs">Rest Period</dt>
                        <dd className="font-medium text-red-900">{selectedEntry.data.restPeriod}h</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <ExternalLink className="h-3 w-3" />
                  <span>Full record: Crew → Hours of Rest</span>
                </div>
              </div>
            )}

            {/* FT-123: Checklist Items Detail (for daily_check and checklist entries) */}
            {(selectedEntry.type === LOGBOOK_ENTRY_TYPES.DAILY_CHECK || selectedEntry.type === LOGBOOK_ENTRY_TYPES.CHECKLIST) &&
              selectedEntry.data?.checks &&
              selectedEntry.data.checks.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Check Items ({selectedEntry.data.checksPassed || selectedEntry.data.checks.filter(c => c.passed).length}/{selectedEntry.data.checksCount || selectedEntry.data.checks.length} passed)
                </p>
                <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200 max-h-48 overflow-y-auto">
                  {selectedEntry.data.checks.map((check, idx) => (
                    <div key={check.id || idx} className="px-3 py-2 flex items-start gap-2">
                      <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                        check.passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {check.passed ? (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${check.passed ? 'text-gray-700' : 'text-red-700 font-medium'}`}>
                          {check.text}
                        </p>
                        {check.notes && (
                          <p className="text-xs text-gray-500 mt-0.5 italic">
                            Note: {check.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Entry Badge - Show for locked entries */}
            {selectedEntry.isLocked && (
              <div className="space-y-3">
                <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-sm px-3 py-1.5 rounded-full border border-emerald-200">
                  <Lock className="h-3 w-3" />
                  <span className="font-medium">Official Record</span>
                  <span className="text-emerald-600">•</span>
                  <span>
                    Signed by {selectedEntry.lockedBy} on {formatFullDateTime(new Date(selectedEntry.lockedAt))}
                  </span>
                </div>
                {/* FT-130: Display Captain's signature if available - check both locations:
                    - selectedEntry.signatureImage (general entries)
                    - selectedEntry.data?.signatureImage (sign-off entries) */}
                {(selectedEntry.signatureImage || selectedEntry.data?.signatureImage) && (
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-1">Captain&apos;s Signature</p>
                    <img
                      src={selectedEntry.signatureImage || selectedEntry.data?.signatureImage}
                      alt="Signature"
                      className="h-12 object-contain"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Audit Information - CRITICAL FOR COMPLIANCE */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Record Information</p>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Recorded by</dt>
                  <dd className="font-medium text-gray-900">
                    {getCreatedByName(selectedEntry.createdBy) || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Date/Time</dt>
                  <dd className="font-medium text-gray-900">
                    {formatFullDateTime(timestamp)}
                  </dd>
                </div>
                {selectedEntry.isAuto && (
                  <>
                    <div>
                      <dt className="text-gray-500">Source</dt>
                      <dd className="font-medium text-gray-900">
                        Auto-logged
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Module</dt>
                      <dd className="font-medium text-gray-900">
                        {selectedEntry.sourceModule}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>

            {/* Entry ID (small footer) */}
            <p className="text-center text-xs text-gray-400 pt-2">
              Entry ID: {selectedEntry.id}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render add entry modal
  const renderAddEntryModal = () => {
    if (!showAddEntry) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => setShowAddEntry(false)}
        />

        <div className="relative w-full max-w-lg bg-white rounded-t-xl sm:rounded-xl shadow-xl">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Add Manual Entry</h3>
            <button
              onClick={() => setShowAddEntry(false)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MANUAL_ENTRY_CATEGORY_LABELS).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setNewEntry(prev => ({ ...prev, category: value }))}
                    className={`px-3 py-2 text-sm rounded-lg border ${
                      newEntry.category === value
                        ? 'border-cyan-600 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entry Content
              </label>
              <textarea
                value={newEntry.content}
                onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter log entry details..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Notes (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes <span className="text-gray-400 text-xs font-normal">(Optional)</span>
              </label>
              <textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional remarks, references, or context..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex gap-3">
            <button
              onClick={() => setShowAddEntry(false)}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddManualEntry}
              className="flex-1 py-3 px-4 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700"
            >
              Add Entry
            </button>
          </div>
        </div>
      </div>
    );
  };

  // FT-196: Sign Off Day handler - now using ConfirmSignOffModal (Pattern 1: confirm-only)
  const handleDaySignOff = useCallback(async (signOffData) => {
    const formalDate = selectedDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Pass null for signatureImage (Pattern 1: no drawn signature)
    const result = await signOffDay(selectedDate, null, signOffData.confirmedBy);

    if (result.success) {
      setShowSignOffDay(false);
      await showAlert(
        'Sign Off Complete',
        `${result.count} entries have been signed and locked for ${formalDate}.`,
        'success'
      );
    } else {
      await showAlert('Error', 'Failed to sign off entries. Please try again.', 'error');
    }
  }, [selectedDate, signOffDay, showAlert]);

  // Render Sign Off Day modal - FT-196: Now uses ConfirmSignOffModal
  const renderSignOffDayModal = () => {
    if (!showSignOffDay) return null;

    const unsignedEntries = getUnsignedEntriesForDate(selectedDate);
    const formalDate = selectedDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // If no unsigned entries, show info modal
    if (unsignedEntries.length === 0) {
      return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSignOffDay(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6">
            <div className="text-center py-4">
              <Lock className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-gray-900 font-medium">All entries signed</p>
              <p className="text-sm text-gray-500 mt-1">
                There are no unsigned entries for {formalDate}.
              </p>
              <button
                onClick={() => setShowSignOffDay(false)}
                className="mt-4 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Show ConfirmSignOffModal for sign-off
    return (
      <ConfirmSignOffModal
        isOpen={showSignOffDay}
        onClose={() => setShowSignOffDay(false)}
        onConfirm={handleDaySignOff}
        title={`Sign Off Daily Log — ${formalDate}`}
        description={`You are confirming that all ${unsignedEntries.length} entries for this date have been reviewed and are accurate. This action will permanently lock these entries (MSN 1905 compliance).`}
      />
    );
  };

  // Render stats summary
  const renderStats = () => (
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {entriesForDate.length} entries on {formatDateDisplay(selectedDate)}
        </span>
        <span className="text-gray-400">
          {stats.last30Days} entries this month
        </span>
      </div>
    </div>
  );

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Logbook tab: [+ Manual] [Export]). Silent no-op when a modal is
  // open (add-entry / export / sign-off-day) so the modal owns focus.
  const isSubView = showAddEntry || showExport || showSignOffDay;
  useSetBottomBarActions(
    isSubView
      ? []
      : [
          {
            key: 'logbook-manual',
            icon: PlusCircle,
            label: 'Manual',
            primary: true,
            onClick: () => setShowAddEntry(true),
          },
          {
            key: 'logbook-export',
            icon: Download,
            label: 'Export',
            onClick: () => setShowExport(true),
          },
        ],
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* FT-232: Conditionally render date header OR filter bar */}
      {isFilterMode ? (
        <>
          {renderFilterBar()}
          {renderToolbar()}
        </>
      ) : (
        <>
          {renderDateHeader()}
          {renderToolbar()}
          {renderStats()}
        </>
      )}

      {/* Entries List */}
      <div className="flex-1 overflow-auto bg-white pb-24 lg:pb-6">
        {renderEntries()}
      </div>

      {/* Add Entry Modal */}
      {renderAddEntryModal()}

      {/* Sign Off Day Modal */}
      {renderSignOffDayModal()}

      {/* Entry Detail Panel */}
      {renderEntryDetail()}
    </div>
  );
};

export default LogbookTab;
